import React, { useState } from 'react';
import { 
  HeartIcon,
  BookmarkIcon,
  BookmarkSlashIcon,
  AcademicCapIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

interface Treatment {
  id: string;
  name: string;
  category: 'drugs' | 'sound_therapy' | 'physiotherapy' | 'medical';
  description: string;
  evidenceLevel: 'experimental' | 'clinical' | 'not_recommended';
  researchLink?: string;
  isBookmarked?: boolean;
}

const TREATMENTS: Treatment[] = [
  // Drugs
  {
    id: 'ginkgo-biloba',
    name: 'Ginkgo Biloba',
    category: 'drugs',
    description: 'Herbal supplement that may improve blood circulation to the inner ear and reduce tinnitus symptoms.',
    evidenceLevel: 'experimental',
    researchLink: '#'
  },
  {
    id: 'magnesium',
    name: 'Magnesium Supplementation',
    category: 'drugs',
    description: 'Magnesium deficiency has been linked to tinnitus. Supplementation may help in cases of deficiency.',
    evidenceLevel: 'clinical',
    researchLink: '#'
  },
  {
    id: 'zinc',
    name: 'Zinc Supplementation',
    category: 'drugs',
    description: 'Zinc plays a role in hearing function. Supplementation may benefit those with zinc deficiency.',
    evidenceLevel: 'experimental',
    researchLink: '#'
  },
  {
    id: 'antidepressants',
    name: 'Antidepressants (SSRIs)',
    category: 'drugs',
    description: 'Selective serotonin reuptake inhibitors may help with tinnitus-related anxiety and depression.',
    evidenceLevel: 'clinical',
    researchLink: '#'
  },

  // Sound Therapy
  {
    id: 'white-noise',
    name: 'White Noise Therapy',
    category: 'sound_therapy',
    description: 'Background white noise can help mask tinnitus and reduce its perceived loudness.',
    evidenceLevel: 'clinical',
    researchLink: '#'
  },
  {
    id: 'pink-noise',
    name: 'Pink Noise Therapy',
    category: 'sound_therapy',
    description: 'Pink noise has been shown to improve sleep quality and may help with tinnitus habituation.',
    evidenceLevel: 'clinical',
    researchLink: '#'
  },
  {
    id: 'notched-music',
    name: 'Notched Music Therapy',
    category: 'sound_therapy',
    description: 'Music with frequencies around the tinnitus frequency removed to promote neural plasticity.',
    evidenceLevel: 'experimental',
    researchLink: '#'
  },
  {
    id: 'tinnitus-retraining',
    name: 'Tinnitus Retraining Therapy (TRT)',
    category: 'sound_therapy',
    description: 'Combination of sound therapy and counseling to help habituate to tinnitus.',
    evidenceLevel: 'clinical',
    researchLink: '#'
  },

  // Physiotherapy
  {
    id: 'tmj-therapy',
    name: 'TMJ/TMD Treatment',
    category: 'physiotherapy',
    description: 'Treatment for temporomandibular joint disorders that may be associated with tinnitus.',
    evidenceLevel: 'clinical',
    researchLink: '#'
  },
  {
    id: 'neck-massage',
    name: 'Neck and Shoulder Massage',
    category: 'physiotherapy',
    description: 'Manual therapy to reduce muscle tension that may contribute to tinnitus.',
    evidenceLevel: 'experimental',
    researchLink: '#'
  },
  {
    id: 'acupuncture',
    name: 'Acupuncture',
    category: 'physiotherapy',
    description: 'Traditional Chinese medicine technique that may help with tinnitus symptoms.',
    evidenceLevel: 'experimental',
    researchLink: '#'
  },
  {
    id: 'cervical-manipulation',
    name: 'Cervical Spine Manipulation',
    category: 'physiotherapy',
    description: 'Chiropractic or osteopathic manipulation to address cervical spine issues.',
    evidenceLevel: 'experimental',
    researchLink: '#'
  },

  // Medical Interventions
  {
    id: 'hearing-aids',
    name: 'Hearing Aids',
    category: 'medical',
    description: 'Amplification of external sounds can help mask tinnitus and improve communication.',
    evidenceLevel: 'clinical',
    researchLink: '#'
  },
  {
    id: 'cochlear-implants',
    name: 'Cochlear Implants',
    category: 'medical',
    description: 'For severe hearing loss, cochlear implants may reduce tinnitus perception.',
    evidenceLevel: 'clinical',
    researchLink: '#'
  },
  {
    id: 'neuromodulation',
    name: 'Transcranial Magnetic Stimulation (TMS)',
    category: 'medical',
    description: 'Non-invasive brain stimulation technique being investigated for tinnitus treatment.',
    evidenceLevel: 'experimental',
    researchLink: '#'
  },
  {
    id: 'surgery',
    name: 'Surgical Interventions',
    category: 'medical',
    description: 'Various surgical procedures for specific causes of tinnitus (rare cases).',
    evidenceLevel: 'not_recommended',
    researchLink: '#'
  }
];

const CATEGORIES = {
  drugs: { name: 'Drugs & Supplements', icon: '💊', color: 'bg-blue-100 text-blue-800' },
  sound_therapy: { name: 'Sound Therapy', icon: '🔊', color: 'bg-green-100 text-green-800' },
  physiotherapy: { name: 'Physiotherapeutic Techniques', icon: '💆', color: 'bg-purple-100 text-purple-800' },
  medical: { name: 'Medical Interventions', icon: '🏥', color: 'bg-red-100 text-red-800' }
};

const EVIDENCE_LEVELS = {
  experimental: { 
    label: 'Experimental', 
    icon: InformationCircleIcon, 
    color: 'text-yellow-600 bg-yellow-100',
    description: 'Limited research, promising but unproven'
  },
  clinical: { 
    label: 'Clinically Used', 
    icon: CheckCircleIcon, 
    color: 'text-green-600 bg-green-100',
    description: 'Supported by clinical evidence'
  },
  not_recommended: { 
    label: 'Not Recommended', 
    icon: XCircleIcon, 
    color: 'text-red-600 bg-red-100',
    description: 'Limited evidence or potential risks'
  }
};

const Treatments: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [bookmarkedTreatments, setBookmarkedTreatments] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  const filteredTreatments = TREATMENTS.filter(treatment => {
    const matchesCategory = !selectedCategory || treatment.category === selectedCategory;
    const matchesSearch = treatment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         treatment.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleBookmark = (treatmentId: string) => {
    setBookmarkedTreatments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(treatmentId)) {
        newSet.delete(treatmentId);
      } else {
        newSet.add(treatmentId);
      }
      return newSet;
    });
  };

  const getBookmarkedTreatments = () => {
    return TREATMENTS.filter(treatment => bookmarkedTreatments.has(treatment.id));
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-auris-dark mb-4">
          Treatment Hub
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Explore various treatment options for tinnitus. Always consult with your healthcare provider 
          before starting any new treatment.
        </p>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search treatments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-auris-teal focus:border-transparent"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                !selectedCategory
                  ? 'bg-auris-teal text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Categories
            </button>
            {Object.entries(CATEGORIES).map(([key, category]) => (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedCategory === key
                    ? 'bg-auris-teal text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <span className="mr-2">{category.icon}</span>
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Bookmarked Treatments */}
      {bookmarkedTreatments.size > 0 && (
        <div className="bg-auris-blue bg-opacity-20 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-auris-dark mb-4 flex items-center">
            <BookmarkIcon className="w-5 h-5 mr-2" />
            Your Bookmarked Treatments ({bookmarkedTreatments.size})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {getBookmarkedTreatments().map((treatment) => (
              <div key={treatment.id} className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold text-auris-dark">{treatment.name}</h3>
                  <button
                    onClick={() => toggleBookmark(treatment.id)}
                    className="text-auris-teal hover:text-auris-dark transition-colors"
                  >
                    <BookmarkIcon className="w-4 h-4 fill-current" />
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-2">{treatment.description}</p>
                <div className="flex items-center justify-between">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${CATEGORIES[treatment.category].color}`}>
                    {CATEGORIES[treatment.category].name}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${EVIDENCE_LEVELS[treatment.evidenceLevel].color}`}>
                    {EVIDENCE_LEVELS[treatment.evidenceLevel].label}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Treatments Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTreatments.map((treatment) => {
          const EvidenceIcon = EVIDENCE_LEVELS[treatment.evidenceLevel].icon;
          const isBookmarked = bookmarkedTreatments.has(treatment.id);
          
          return (
            <div key={treatment.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-auris-dark">{treatment.name}</h3>
                <button
                  onClick={() => toggleBookmark(treatment.id)}
                  className={`transition-colors ${
                    isBookmarked ? 'text-auris-teal' : 'text-gray-400 hover:text-auris-teal'
                  }`}
                >
                  {isBookmarked ? (
                    <BookmarkIcon className="w-5 h-5 fill-current" />
                  ) : (
                    <BookmarkSlashIcon className="w-5 h-5" />
                  )}
                </button>
              </div>

              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                {treatment.description}
              </p>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${CATEGORIES[treatment.category].color}`}>
                    <span className="mr-1">{CATEGORIES[treatment.category].icon}</span>
                    {CATEGORIES[treatment.category].name}
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  <EvidenceIcon className="w-4 h-4" />
                  <span className={`px-2 py-1 rounded text-xs font-medium ${EVIDENCE_LEVELS[treatment.evidenceLevel].color}`}>
                    {EVIDENCE_LEVELS[treatment.evidenceLevel].label}
                  </span>
                </div>

                <p className="text-xs text-gray-500">
                  {EVIDENCE_LEVELS[treatment.evidenceLevel].description}
                </p>

                {treatment.researchLink && (
                  <a
                    href={treatment.researchLink}
                    className="inline-flex items-center text-sm text-auris-teal hover:text-auris-dark transition-colors"
                  >
                    <AcademicCapIcon className="w-4 h-4 mr-1" />
                    View Research
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filteredTreatments.length === 0 && (
        <div className="text-center py-12">
          <HeartIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No treatments found</h3>
          <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
        </div>
      )}

      {/* Disclaimer */}
      <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-start">
          <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600 mr-3 mt-0.5" />
          <div>
            <h3 className="font-medium text-yellow-900 mb-2">Important Disclaimer</h3>
            <p className="text-sm text-yellow-800">
              The information provided here is for educational purposes only and should not be considered 
              medical advice. Always consult with a qualified healthcare professional before starting any 
              new treatment. Individual results may vary, and some treatments may not be suitable for everyone.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Treatments;
